import Matter from "matter-js";
import type { GameConfig } from "../config/GameConfig";
import type { WorldBounds } from "../core/WorldBounds";

export type CollisionHandler = (
  bodyA: Matter.Body,
  bodyB: Matter.Body,
) => void;

export class PhysicsWorld {
  readonly engine: Matter.Engine;
  readonly world: Matter.World;
  private walls: Matter.Body[] = [];
  private collisionHandlers: CollisionHandler[] = [];

  constructor(
    private readonly config: GameConfig,
    private readonly bounds: WorldBounds,
  ) {
    this.engine = Matter.Engine.create({
      gravity: {
        x: config.physics.gravityX,
        y: config.physics.gravityY,
      },
    });
    this.world = this.engine.world;
    this.engine.enableSleeping = false;

    Matter.Events.on(this.engine, "collisionStart", (event) => {
      for (const pair of event.pairs) {
        for (const handler of this.collisionHandlers) {
          handler(pair.bodyA, pair.bodyB);
        }
      }
    });

    this.rebuildWalls();
    this.bounds.onResize(() => this.rebuildWalls());
  }

  onCollision(handler: CollisionHandler): void {
    this.collisionHandlers.push(handler);
  }

  add(body: Matter.Body): void {
    Matter.Composite.add(this.world, body);
  }

  remove(body: Matter.Body): void {
    Matter.Composite.remove(this.world, body);
  }

  update(deltaMs: number): void {
    this.engine.gravity.x = this.config.physics.gravityX;
    this.engine.gravity.y = this.config.physics.gravityY;
    Matter.Engine.update(this.engine, deltaMs);
  }

  rebuildWalls(): void {
    for (const wall of this.walls) {
      Matter.Composite.remove(this.world, wall);
    }
    this.walls.length = 0;

    const t = this.config.physics.wallThickness;
    const w = this.bounds.width;
    const h = this.bounds.height;
    const opts: Matter.IChamferableBodyDefinition = {
      isStatic: true,
      restitution: 0.4,
      friction: 0.02,
      label: "wall",
    };

    this.walls = [
      Matter.Bodies.rectangle(w * 0.5, -t * 0.5, w + t * 2, t, opts),
      Matter.Bodies.rectangle(w * 0.5, h + t * 0.5, w + t * 2, t, opts),
      Matter.Bodies.rectangle(-t * 0.5, h * 0.5, t, h + t * 2, opts),
      Matter.Bodies.rectangle(w + t * 0.5, h * 0.5, t, h + t * 2, opts),
    ];
    Matter.Composite.add(this.world, this.walls);
  }
}
